'use client';

import Link from 'next/link';
import { Shield, BarChart3, EyeOff, Network, Bot, KeyRound, Sparkles, ScanSearch, Radar } from 'lucide-react';

export default function FeaturesSection() {
  return (
    <section id="features" className="sm:py-24 pt-16 pb-16 relative scroll-mt-20">
      <div className="sm:px-6 lg:px-8 max-w-7xl mx-auto px-4">
        <div className="text-center mb-16 relative">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-rose-400 px-3 py-1 rounded-none bg-rose-500/10 border border-rose-500/20">
            Features
          </span>
          <h2 className="sm:text-4xl md:text-5xl text-3xl font-semibold tracking-tight text-center mt-4 text-white">
            Security a WAF doesn&apos;t give you
          </h2>
          <p className="sm:text-lg text-base text-neutral-400 text-center mt-4 max-w-2xl mx-auto leading-relaxed">
            Silker is built for apps with AI in them. It blocks prompt injection, stops sensitive data from leaking, and covers the OWASP basics - all from one engine.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* AI-native */}
          <div className="relative bg-neutral-900/20 border-white/5 border rounded-none p-8 overflow-hidden group hover:border-rose-500/20 transition-colors duration-300">
            <div className="relative z-10">
              <div className="w-12 h-12 rounded-none bg-rose-500/10 border border-rose-500/20 flex items-center justify-center mb-6">
                <Bot className="w-6 h-6 text-rose-400" />
              </div>
              <h3 className="text-2xl font-semibold mb-3 text-white">AI-Native Threat Detection</h3>
              <p className="text-sm text-neutral-400 mb-8 leading-relaxed">
                The threats unique to AI-generated and AI-powered apps - caught before they reach your model or database.
              </p>

              <ul className="space-y-4 text-sm text-neutral-300">
                <li className="flex items-start gap-3">
                  <div className="p-1 rounded-none bg-rose-500/10 border border-rose-500/20 mt-0.5">
                    <Sparkles className="w-3.5 h-3.5 text-rose-400" />
                  </div>
                  <div>
                    <strong className="text-white block mb-0.5">Prompt Injection &amp; Jailbreaks</strong>
                    <span className="text-neutral-500">Detects instruction overrides, jailbreaks and extraction on your LLM routes.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="p-1 rounded-none bg-rose-500/10 border border-rose-500/20 mt-0.5">
                    <Shield className="w-3.5 h-3.5 text-rose-400" />
                  </div>
                  <div>
                    <strong className="text-white block mb-0.5">OWASP Top 10</strong>
                    <span className="text-neutral-500">SQLi, XSS, path traversal, file-upload abuse and more - included by default.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="p-1 rounded-none bg-rose-500/10 border border-rose-500/20 mt-0.5">
                    <Network className="w-3.5 h-3.5 text-rose-400" />
                  </div>
                  <div>
                    <strong className="text-white block mb-0.5">Rate Limiting &amp; Auto IP Bans</strong>
                    <span className="text-neutral-500">
                      Stops abuse and brute-force with sliding-window limits and temporary bans - core steps in{' '}
                      <Link href="/guides/how-to-secure-an-api" className="text-neutral-300 underline underline-offset-4 hover:text-white">
                        how to secure an API
                      </Link>.
                    </span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="p-1 rounded-none bg-rose-500/10 border border-rose-500/20 mt-0.5">
                    <Radar className="w-3.5 h-3.5 text-rose-400" />
                  </div>
                  <div>
                    <strong className="text-white block mb-0.5">Scanner Trap <span className="text-[10px] uppercase tracking-wider text-rose-400/80 border border-rose-500/30 px-1.5 py-0.5 ml-1 align-middle">New</span></strong>
                    <span className="text-neutral-500">Honeypot paths (/.env, /wp-login.php, /.git) bots probe first - hit one and we ban the IP instantly, before it finds a real hole.</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Data leak prevention */}
          <div className="relative bg-neutral-900/20 border-white/5 border rounded-none p-8 overflow-hidden group hover:border-blue-500/20 transition-colors duration-300">
            <div className="relative z-10">
              <div className="w-12 h-12 rounded-none bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-6">
                <EyeOff className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-2xl font-semibold mb-3 text-white">Data Leak Prevention</h3>
              <p className="text-sm text-neutral-400 mb-8 leading-relaxed">
                The part a WAF can&apos;t see: sensitive data leaving your app. Silker inspects responses and outbound calls -{' '}
                <Link href="/vs/cloudflare-waf" className="text-neutral-200 underline underline-offset-4 hover:text-white">
                  why edge WAFs miss outbound data
                </Link>{' '}
                - then redacts or blocks leaks.
              </p>

              <ul className="space-y-4 text-sm text-neutral-300">
                <li className="flex items-start gap-3">
                  <div className="p-1 rounded-none bg-blue-500/10 border border-blue-500/20 mt-0.5">
                    <KeyRound className="w-3.5 h-3.5 text-blue-400" />
                  </div>
                  <div>
                    <strong className="text-white block mb-0.5">Secrets &amp; API Keys</strong>
                    <span className="text-neutral-500">Catches leaked keys, tokens, private keys and DB connection strings.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="p-1 rounded-none bg-blue-500/10 border border-blue-500/20 mt-0.5">
                    <EyeOff className="w-3.5 h-3.5 text-blue-400" />
                  </div>
                  <div>
                    <strong className="text-white block mb-0.5">PII Redaction</strong>
                    <span className="text-neutral-500">Emails, phone numbers, cards, SSN/PESEL - block, redact or just monitor.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="p-1 rounded-none bg-blue-500/10 border border-blue-500/20 mt-0.5">
                    <ScanSearch className="w-3.5 h-3.5 text-blue-400" />
                  </div>
                  <div>
                    <strong className="text-white block mb-0.5">On-demand Security Scans</strong>
                    <span className="text-neutral-500">Scan your live app for misconfigurations and exposed surface, with fix tips.</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <div className="relative bg-neutral-900/20 border-white/5 border rounded-none p-8 overflow-hidden group hover:border-emerald-500/20 transition-colors duration-300">
            <div className="relative z-10 flex flex-col lg:flex-row items-start gap-12">
              <div className="flex-1">
                 <div className="w-12 h-12 rounded-none bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-6">
                  <BarChart3 className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-2xl font-semibold mb-3 text-white">Monitoring &amp; Alerts</h3>
                <p className="text-sm text-neutral-400 mb-6 leading-relaxed">
                  One dashboard for every install - SDK, Worker or container. Deploy Silker as a{' '}
                  <Link href="/guides/what-is-a-reverse-proxy" className="text-neutral-200 underline underline-offset-4 hover:text-white">
                    reverse proxy
                  </Link>{' '}
                  in front of any backend and see blocked threats, data-leak alerts, traffic and geographic origins in real-time.
                </p>
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-neutral-300">
                   <span className="flex items-center gap-2">
                     <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></div>
                     Real-time Logs
                   </span>
                   <span className="flex items-center gap-2">
                     <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                     Traffic Analytics
                   </span>
                   <span className="flex items-center gap-2">
                     <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                     Threat Map
                   </span>
                   <span className="flex items-center gap-2">
                     <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                     AI Copilot
                   </span>
                </div>
              </div>

              <div className="flex-1 w-full bg-black/40 rounded-none border border-white/5 p-5 font-mono text-xs">
                 <div className="text-neutral-500 mb-3 uppercase tracking-wider font-semibold">Live Activity Feed</div>
                 <div className="space-y-3">
                    <div className="flex justify-between items-center p-2 rounded-none bg-emerald-500/5 border border-emerald-500/10">
                       <span className="text-emerald-400">GET /api/chat</span>
                       <span className="text-emerald-400">200 OK</span>
                    </div>
                    <div className="flex justify-between items-center p-2 rounded-none bg-rose-500/5 border border-rose-500/10">
                       <span className="text-rose-400">POST /v1/chat <span className="opacity-50">(prompt injection)</span></span>
                       <span className="text-rose-400">403 BLOCKED</span>
                    </div>
                    <div className="flex justify-between items-center p-2 rounded-none bg-amber-500/5 border border-amber-500/10">
                       <span className="text-amber-400">GET /api/users <span className="opacity-50">(PII leak)</span></span>
                       <span className="text-amber-400">REDACTED</span>
                    </div>
                    <div className="flex justify-between items-center p-2 rounded-none bg-rose-500/5 border border-rose-500/10">
                       <span className="text-rose-400">POST /api/admin <span className="opacity-50">(SQLi)</span></span>
                       <span className="text-rose-400">403 BLOCKED</span>
                    </div>
                 </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
